import { assertCents, type Cents } from './money'
import { ENDED_WITHOUT_RENTAL, type ReservationStatus } from './reservation-status'

/**
 * What a booking's account adds up to, from its parts. The one formula the
 * counter, the e-mails, the payment page and the Today board all read
 * (`ledger` in the server), so no screen can tell another story.
 */
export interface AccountParts {
  status: ReservationStatus
  totalCents: Cents
  /** Set exactly on a cancelled or no-show booking (the database checks it). */
  cancellationFeeCents: Cents | null
  /** OPS-3: added at the return. */
  chargesCents: Cents
  /** Every payment, the part of a deposit kept included. */
  paidCents: Cents
  /** Refunds that count: done, or on their way (LIVE_REFUND_STATES). */
  refundedCents: Cents
  /**
   * S5: what the customer's bank took back through a dispute it decided for
   * them (`disputeEffect().returned`): gone like a refund, so owed again.
   */
  lostInDisputesCents: Cents
}

export interface Account {
  /** What the booking costs: its total, or, once it ended without a rental, its fee. */
  dueCents: Cents
  /**
   * Due and charges, less what was paid and not given back, by us or by a
   * dispute. Negative: owed to the customer.
   */
  balanceCents: Cents
}

export function accountOf(parts: AccountParts): Account {
  let dueCents = assertCents(parts.totalCents, 'total')
  if (ENDED_WITHOUT_RENTAL.includes(parts.status)) {
    if (parts.cancellationFeeCents === null) {
      throw new Error(`a ${parts.status} booking without its cancellation fee`)
    }
    dueCents = assertCents(parts.cancellationFeeCents, 'fee')
  }
  const balanceCents =
    dueCents +
    assertCents(parts.chargesCents, 'charges') -
    assertCents(parts.paidCents, 'paid') +
    assertCents(parts.refundedCents, 'refunded') +
    assertCents(parts.lostInDisputesCents, 'lost in disputes')
  return { dueCents, balanceCents: assertCents(balanceCents, 'balance') }
}
